/** Mapping tables (M15-35): key → account and/or dimension, versioned and approved. */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Upload } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { shortDate } from '../../lib/format';
import { manilaToday, useAccounts } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { ReasonDialog } from '../../components/ReasonDialog';
import { RuleStatusChip, errorText, type RuleStatus } from './shared';

interface Entry { keys: Record<string, string>; account?: string; dimensions?: Record<string, string> }
interface TableVersion { id: string; version: number; status: RuleStatus; effectiveFrom: string; entries: Entry[]; createdById: string; submittedById: string | null; approvedById: string | null; activatedAt: string | null; createdAt: string }
export interface MappingTable { id: string; code: string; name: string; keyFields: string[]; versions: TableVersion[] }

export function useMappingTables() {
  return useQuery({ queryKey: ['mapping-tables'], queryFn: () => api<MappingTable[]>('/mapping-tables') });
}

function NewTable({ onClose }: { onClose: (id?: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [keys, setKeys] = useState('');
  const save = useMutation({
    mutationFn: () => api<{ id: string }>('/mapping-tables', { method: 'POST', body: { code, name, keyFields: keys.split(/[\s,]+/).filter(Boolean) } }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['mapping-tables'] }); onClose(r.id); },
  });
  return (
    <Modal title="New mapping table" onClose={() => onClose()}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <Alert tone="critical">{errorText(save.error)}</Alert>}
        <Field label="Code" required hint="Upper case, used in rules: TENDER_METHOD"><input className={inputClass} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required /></Field>
        <Field label="Name" required><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required placeholder="Tender method → clearing account" /></Field>
        <Field label="Key fields" required hint="Lower-case names, comma-separated: method  ·  department, branch"><input className={inputClass} value={keys} onChange={(e) => setKeys(e.target.value)} required /></Field>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => onClose()}>Cancel</Button><Button type="submit" busy={save.isPending}>Create</Button></div>
      </form>
    </Modal>
  );
}

export function MappingTablesPage(): JSX.Element {
  const { can, user } = useAuth();
  const qc = useQueryClient();
  const tables = useMappingTables();
  const accounts = useAccounts();
  const [tableId, setTableId] = useState<string | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [rows, setRows] = useState<Entry[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState(manilaToday());
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical'; text: string } | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [csv, setCsv] = useState<string | null>(null);

  const table = tables.data?.find((t) => t.id === tableId) ?? tables.data?.[0];
  const version = table?.versions.find((v) => v.id === versionId) ?? table?.versions[0];
  const editable = !version || version.status === 'DRAFT' || version.status === 'ACTIVE' || version.status === 'APPROVED' || version.status === 'RETIRED';
  const draft = !version || version.status === 'DRAFT';

  useEffect(() => {
    setRows(version?.entries ?? []);
    setEffectiveFrom(version?.status === 'DRAFT' ? version.effectiveFrom : manilaToday());
    setMessage(null);
  }, [version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => void qc.invalidateQueries({ queryKey: ['mapping-tables'] });
  const save = useMutation({
    mutationFn: () => api<{ id: string; version: number }>(`/mapping-tables/${table!.id}/entries`, { method: 'PUT', body: { effectiveFrom, entries: rows.map((r) => ({ keys: r.keys, ...(r.account ? { account: r.account } : {}), ...(r.dimensions && Object.keys(r.dimensions).length ? { dimensions: r.dimensions } : {}) })) } }),
    onSuccess: (r) => { refresh(); setVersionId(r.id); setMessage({ tone: 'ok', text: `Saved as draft version ${r.version}. Submit it for approval when ready.` }); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });
  const step = useMutation({
    mutationFn: ({ action, body }: { action: string; body?: unknown }) => api(`/mapping-tables/versions/${version!.id}/${action}`, { method: 'POST', body }),
    onSuccess: (_, v) => { refresh(); setRejecting(false); setMessage({ tone: 'ok', text: { submit: 'Submitted for approval.', approve: 'Approved. Activate it to use it from its effective date.', activate: 'Active.', reject: 'Sent back.', retire: 'Retired.' }[v.action] ?? 'Done.' }); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });

  const importCsv = (text: string) => {
    if (!table) return;
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const header = lines[0].split(',').map((h) => h.trim());
    const parsed = lines.slice(1).map((l) => {
      const cells = l.split(',').map((c) => c.trim());
      const at = (h: string) => cells[header.indexOf(h)] ?? '';
      const dims = Object.fromEntries(header.filter((h) => h.startsWith('dim:')).map((h) => [h.slice(4), at(h)]).filter(([, v]) => v));
      return { keys: Object.fromEntries(table.keyFields.map((k) => [k, at(k)])), account: at('account') || undefined, dimensions: dims };
    });
    setRows(parsed);
    setCsv(null);
    setMessage({ tone: 'ok', text: `${parsed.length} rows loaded. Review, then save as a draft version.` });
  };

  const mine = version && (version.createdById === user?.id || version.submittedById === user?.id);
  const postable = (accounts.data ?? []).filter((a) => a.isPosting && a.active && (!a.isControl || a.isTaxAccount));

  return (
    <>
      <PageHeader title="Mapping Tables" description="Look-ups rules use to pick accounts and dimensions: tender → clearing account, department → cost centre. An unmapped key waits in the exception queue." actions={can('rules.edit') && <Button onClick={() => setCreating(true)}><Plus aria-hidden="true" className="h-4 w-4" /> New table</Button>} />
      {tables.isLoading && <Skeleton className="h-40 w-full" />}
      {tables.data?.length === 0 && <Card><EmptyState title="No mapping tables" description="Create one, e.g. TENDER_METHOD keyed by method." /></Card>}
      {table && (
        <div className="grid gap-4 lg:grid-cols-4">
          <Card title="Tables" className="lg:col-span-1">
            <ul className="space-y-1">
              {tables.data?.map((t) => (
                <li key={t.id}>
                  <button onClick={() => { setTableId(t.id); setVersionId(null); }} className={`w-full rounded-lg px-3 py-2 text-left text-sm ${t.id === table.id ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-brand-100' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                    <code className="text-xs">{t.code}</code>
                    <span className="block text-xs text-slate-500">{t.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          <div className="space-y-4 lg:col-span-3">
            {message && <Alert tone={message.tone}>{message.text}</Alert>}
            <Card
              title={`${table.code} · keyed by ${table.keyFields.join(', ')}`}
              description={version ? `Version ${version.version}, effective ${shortDate(version.effectiveFrom)}` : 'No versions yet'}
              actions={version && (
                <div className="flex flex-wrap items-center gap-2">
                  <RuleStatusChip status={version.status} />
                  {version.status === 'DRAFT' && can('rules.edit') && <Button size="sm" variant="secondary" onClick={() => step.mutate({ action: 'submit' })}>Submit</Button>}
                  {version.status === 'SUBMITTED' && can('rules.approve') && !mine && <Button size="sm" onClick={() => step.mutate({ action: 'approve' })}>Approve</Button>}
                  {version.status === 'SUBMITTED' && can('rules.approve') && !mine && <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>Send back</Button>}
                  {version.status === 'APPROVED' && can('rules.approve') && <Button size="sm" onClick={() => step.mutate({ action: 'activate' })}>Activate</Button>}
                  {version.status === 'ACTIVE' && can('rules.approve') && <Button size="sm" variant="ghost" onClick={() => step.mutate({ action: 'retire' })}>Retire</Button>}
                </div>
              )}
            >
              {table.versions.length > 1 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {table.versions.map((v) => (
                    <button key={v.id} onClick={() => setVersionId(v.id)} className={`rounded-lg border px-2 py-1 text-xs ${v.id === version?.id ? 'border-brand font-semibold' : 'border-slate-200 dark:border-slate-700'}`}>v{v.version} · {v.status.toLowerCase()}</button>
                  ))}
                </div>
              )}
              {!draft && version && <p className="mb-3 text-sm text-slate-500">This version is {version.status.toLowerCase()} and fixed. Editing below and saving creates a new draft version.</p>}
              <TableWrap>
                <thead><tr>{table.keyFields.map((k) => <Th key={k}>{k}</Th>)}<Th>Account</Th><Th>Dimensions</Th><Th> </Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rows.map((r, i) => (
                    <tr key={i}>
                      {table.keyFields.map((k) => (
                        <Td key={k}><input className={filterClass + ' w-32 font-mono'} value={r.keys[k] ?? ''} disabled={!editable || version?.status === 'SUBMITTED'} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, keys: { ...x.keys, [k]: e.target.value } } : x)))} aria-label={k} /></Td>
                      ))}
                      <Td>
                        <select className={filterClass + ' w-56'} value={r.account ?? ''} disabled={version?.status === 'SUBMITTED'} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, account: e.target.value || undefined } : x)))} aria-label="Account">
                          <option value="">—</option>
                          {postable.map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
                        </select>
                      </Td>
                      <Td><input className={filterClass + ' w-40 font-mono'} defaultValue={Object.entries(r.dimensions ?? {}).map(([k, v]) => `${k}=${v}`).join('; ')} disabled={version?.status === 'SUBMITTED'} placeholder="DEPT=LAB" onBlur={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, dimensions: Object.fromEntries(e.target.value.split(/[;,]/).map((p) => p.split('=').map((s) => s.trim())).filter((p) => p.length === 2 && p[0] && p[1])) } : x)))} aria-label="Dimensions" /></Td>
                      <Td>{version?.status !== 'SUBMITTED' && <Button size="sm" variant="ghost" aria-label="Remove row" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 aria-hidden="true" className="h-4 w-4" /></Button>}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
              {rows.length === 0 && <p className="py-4 text-center text-sm text-slate-500">No rows. Use * as a key to match anything.</p>}
              {can('rules.edit') && version?.status !== 'SUBMITTED' && (
                <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setRows([...rows, { keys: Object.fromEntries(table.keyFields.map((k) => [k, ''])) }])}><Plus aria-hidden="true" className="h-4 w-4" /> Row</Button>
                    <Button size="sm" variant="secondary" onClick={() => setCsv(`${table.keyFields.join(',')},account,dim:BRANCH\n`)}><Upload aria-hidden="true" className="h-4 w-4" /> Paste CSV</Button>
                  </div>
                  <div className="flex items-end gap-2">
                    <Field label="Effective from"><input className={filterClass} type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} /></Field>
                    <Button busy={save.isPending} onClick={() => save.mutate()}>{draft ? 'Save draft' : 'Save as new version'}</Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
      {creating && <NewTable onClose={(id) => { setCreating(false); if (id) { setTableId(id); setVersionId(null); } }} />}
      {rejecting && <ReasonDialog title="Send back" confirmLabel="Send back" tone="primary" busy={step.isPending} onClose={() => setRejecting(false)} onConfirm={(reason) => step.mutate({ action: 'reject', body: { reason } })} />}
      {csv !== null && (
        <Modal title="Paste rows" description="First line is the header: the key fields, then account, then optional dim:CODE columns. Replaces the rows on screen." onClose={() => setCsv(null)}>
          <textarea className={inputClass + ' font-mono text-xs'} rows={10} value={csv} onChange={(e) => setCsv(e.target.value)} />
          <div className="mt-3 flex justify-end gap-2"><Button variant="secondary" onClick={() => setCsv(null)}>Cancel</Button><Button onClick={() => importCsv(csv)}>Load rows</Button></div>
        </Modal>
      )}
    </>
  );
}
